import { createEffect, createMemo, createResource, createSignal, Show, untrack } from 'solid-js'
import { EmailRecipient, Message, TicketEmailDraft, supportAssistAgent as api } from 'st-app/api'
import { showModal } from '../../st-modal/st-modal'
import styles from '../st-support-assist-agent.module.css'
import Button from '../../st-button/st-button'
import Loader from '../../st-loader/st-loader'
import Quill from 'quill'
import { toast } from 'common'

import 'quill/dist/quill.snow.css'
import { requireSuppportAssistAgentContext } from '../SupportAssistAgentContext'
import { createStore } from 'solid-js/store'
import RecipientInput from './RecipientInput'

export function showEmailDraftModal(message: Message) {
    const context = requireSuppportAssistAgentContext();
    const ticketGU = context.ticketGU();
    if (!ticketGU)
        throw new Error('No ticketGU found in context. Cannot show email draft modal.');

    const dispose = showModal(() => {
        const [allStaff] = createResource(() => api.GetAllStaff(ticketGU));
        const [sending, setSending] = createSignal(false);
        const [loading, setLoading] = createSignal(true);

        const [draft, setDraft] = createStore<TicketEmailDraft>({
            subject: '',
            body: '',
            bodyHtml: '',
            to: [],
            bcc: [],
         });

        const [to, setTo] = createSignal<EmailRecipient[]>([]);
        const [bcc, setBcc] = createSignal<EmailRecipient[]>([]);

        let editor: Quill | undefined;

        const initEditor = (container: HTMLDivElement) => {
            setTimeout(async () => {
                const el = document.createElement('div');
                container.replaceChildren(el);

                editor = new Quill(el, {
                    theme: 'snow',
                    modules: {
                        toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline'],
                            [{ align: [] }],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            [{ color: [] }, { background: [] }],
                            ['link'],
                            ['clean'],
                        ],
                    },
                    formats: [
                        'header', 'bold', 'italic', 'underline',
                        'align', 'list', 'color', 'background', 'link',
                    ],
                });

                // Send message content to backend for AI email generation + templating
                try {
                    const response = await api.GetEmailDraft(ticketGU, message.text || '');
                    setDraft(response);

                    // Seed To from clientEmail; effect will enrich with full staff data once allStaff loads
                    if (response.clientEmail) {
                        setTo([{ email: response.clientEmail, userGU: response.clientUserGU, isEdupointStaff: false }]);
                    }

                    editor.clipboard.dangerouslyPasteHTML(draft?.bodyHtml || draft?.body || '');
                } catch {
                    editor.clipboard.dangerouslyPasteHTML(message.text || '');
                    toast.notify('error', 'Failed to generate email draft. The message content has been pre-filled instead.');
                } finally {
                    setLoading(false);
                }
            });
        };

        const getEditorValue = (): string => {
            if (!editor) return '';
            return editor.getSemanticHTML();
        };

        const handleSend = async () => {
            setSending(true);
            try {
                const body = getEditorValue();
                setDraft('bodyHtml', body);
                setDraft('to', to());
                setDraft('bcc', bcc());
                await api.SendEmail(draft);
                toast.notify('success', 'Email sent successfully.');
                dispose();
            } catch {
                toast.notify('error', 'Failed to send email. Please try again.');
            } finally {
                setSending(false);
            }
        };

        return (
            <div class={styles['email-draft-modal']}>
                <div class={styles['email-draft-fields']}>
                    <label class={styles['email-draft-field-label']}>To</label>
                    <RecipientInput value={[to, setTo]} allRecipients={() => allStaff() ?? []} placeholder="Search staff or enter email..." />
                    <label class={styles['email-draft-field-label']}>BCC</label>
                    <RecipientInput value={[bcc, setBcc]} allRecipients={() => allStaff() ?? []} placeholder="Search staff or enter email..." />
                    <label class={styles['email-draft-field-label']}>Subject</label>
                    <input class={styles['email-draft-field-input']} type="text"
                        value={draft.subject ?? ''} onInput={(e) => setDraft('subject', e.currentTarget.value)} />
                </div>
                <label class={styles['email-draft-label']}>Body</label>
                <div class={styles['email-draft-editor-wrapper']}>
                    <div class={styles['email-draft-editor-container']} ref={initEditor} />
                    <Show when={loading()}>
                        <div class={styles['email-draft-loading-overlay']}>
                            <Loader size="medium" label="Generating email draft..." />
                        </div>
                    </Show>
                </div>
                <div class={styles['email-draft-actions']}>
                    <Button type="default" onClick={() => dispose()}>Cancel</Button>
                    <Button type="primary" loading={sending() || loading()} onClick={handleSend}>Send</Button>
                </div>
            </div>
        );
    }, {
        title: 'Draft Email',
        class: styles['email-draft-modal-wrapper'],
    });
}
